import { useEffect, useState } from 'react';

import { campaignService } from 'services/campaign/campaignService';

import { searchService } from './searchService';
import { CampaignsState, SearchCampaignsParams } from './searchService.dto';
import { sortByIds } from './searchSorting';

const initialState: CampaignsState = {
  campaigns: [],
  page: 0,
  loading: false,
  error: false,
  hasNextPage: true,
};

const useSearchCampaigns = (searchParams: SearchCampaignsParams) => {
  const [state, setState] = useState<CampaignsState>(initialState);

  useEffect(() => {
    if (state.page === 0) {
      fetchNextPage();
    }
  }, [state.page]);

  const loadData = async () => {
    const searchResult = await searchService.searchCampaigns({ ...searchParams, page: state.page });

    const { last, content } = searchResult.data;
    const ids = content?.map(campaign => campaign.id);

    if (!!ids && ids.length > 0) {
      const campaignDetails = await campaignService.fetchBulkCampaignDetails(ids);
      const sortedCampaignDetails = sortByIds(ids, campaignDetails.data);

      return { data: sortedCampaignDetails, last };
    } else {
      return { data: [], last };
    }
  };

  const fetchNextPage = async () => {
    if (!state.hasNextPage || state.loading) return;

    setState(prevState => ({ ...prevState, loading: true }));

    try {
      const { data, last } = await loadData();
      setState(prevState => ({
        ...prevState,
        campaigns: prevState.page === 0 ? data : [...prevState.campaigns, ...data],
        hasNextPage: !last,
        page: prevState.page + 1,
      }));
    } catch (error) {
      setState(prevState => ({ ...prevState, error: true }));
    } finally {
      setState(prevState => ({ ...prevState, loading: false }));
    }
  };

  const refresh = () => {
    setState(initialState);
  };

  return {
    fetchNextPage,
    state,
    refresh,
  };
};

export { useSearchCampaigns };
